import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api, formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Users, Package, Gavel, Truck, Dna, CreditCard, Bot, Shield,
  AlertTriangle, AlertCircle, Info, Activity, Loader2,
  TrendingUp, Zap, Heart, Clock, ChevronRight, Trash2,
  RotateCcw, Eye, ShieldCheck,
} from 'lucide-react';

const ALERT_STYLES = {
  critical: { bg: 'bg-red-500/8 border-red-500/20', text: 'text-red-400', icon: AlertTriangle },
  warning: { bg: 'bg-amber-500/8 border-amber-500/20', text: 'text-amber-400', icon: AlertCircle },
  info: { bg: 'bg-emerald-500/8 border-emerald-500/20', text: 'text-emerald-400', icon: Info },
};

const EVENT_ICONS = {
  new_user: Users, new_listing: Package, purchase: ShieldCheck,
  ai_convo: Bot, enbixos: Truck, audit: Shield,
};

export default function AdminDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [activity, setActivity] = useState([]);
  const [health, setHealth] = useState(null);
  const [aiData, setAiData] = useState(null);
  const [quickStats, setQuickStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [m, a, act, h, ai, qs] = await Promise.all([
        api.adminDashboard().catch(() => null),
        api.adminAlerts().catch(() => ({ alerts: [] })),
        api.adminActivity().catch(() => ({ events: [] })),
        api.adminHealth().catch(() => null),
        api.adminAIMonitoring().catch(() => null),
        api.adminQuickStats().catch(() => null),
      ]);
      setMetrics(m);
      setAlerts(a.alerts || []);
      setActivity(act.events || []);
      setHealth(h);
      setAiData(ai);
      setQuickStats(qs);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen"><Loader2 className="w-8 h-8 text-red-400 animate-spin" /></div>
  );

  const criticalAlerts = alerts.filter(a => a.level === 'critical');
  const warningAlerts = alerts.filter(a => a.level === 'warning');
  const infoAlerts = alerts.filter(a => a.level === 'info');

  const healthColor = health?.overall >= 90 ? 'text-emerald-400' : health?.overall >= 70 ? 'text-amber-400' : 'text-red-400';
  const healthBg = health?.overall >= 90 ? 'bg-emerald-500/10 border-emerald-500/20' : health?.overall >= 70 ? 'bg-amber-500/10 border-amber-500/20' : 'bg-red-500/10 border-red-500/20';

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-100 tracking-tight">Centro de Control</h1>
          <p className="text-xs text-zinc-600 mt-0.5">MorphMX Mission Control</p>
        </div>
        <div className="flex items-center gap-3">
          {health && (
            <div className={`flex items-center gap-2 px-4 py-2 rounded-xl border ${healthBg}`} data-testid="health-score">
              <Activity className={`w-4 h-4 ${healthColor}`} />
              <span className={`text-sm font-medium ${healthColor}`}>{health.overall}%</span>
              <span className="text-xs text-zinc-500">{health.label}</span>
            </div>
          )}
          <Button variant="outline" size="sm" onClick={loadAll} className="border-white/5 text-zinc-500 text-xs">
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Actualizar
          </Button>
        </div>
      </div>

      {/* CRITICAL ALERTS */}
      {criticalAlerts.length > 0 && (
        <div className="mb-6 space-y-2" data-testid="critical-alerts">
          {criticalAlerts.map((alert, i) => {
            const style = ALERT_STYLES.critical;
            const Icon = style.icon;
            return (
              <div key={`critical-${alert.message}`} className={`flex items-center gap-3 p-3.5 rounded-xl border ${style.bg}`}>
                <Icon className={`w-5 h-5 ${style.text} flex-shrink-0`} />
                <span className={`text-sm flex-1 ${style.text}`}>{alert.message}</span>
                {alert.action && (
                  <Link to={alert.action}>
                    <Button size="sm" variant="ghost" className="text-red-400 text-xs h-7 hover:bg-red-500/10">
                      Atender <ChevronRight className="w-3 h-3 ml-0.5" />
                    </Button>
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* WARNING ALERTS */}
      {warningAlerts.length > 0 && (
        <div className="mb-6 space-y-2" data-testid="warning-alerts">
          {warningAlerts.map((alert, i) => {
            const style = ALERT_STYLES.warning;
            const Icon = style.icon;
            return (
              <div key={`warn-${alert.message}`} className={`flex items-center gap-3 p-3 rounded-xl border ${style.bg}`}>
                <Icon className={`w-4 h-4 ${style.text} flex-shrink-0`} />
                <span className={`text-sm flex-1 ${style.text}`}>{alert.message}</span>
                {alert.action && (
                  <Link to={alert.action}>
                    <Button size="sm" variant="ghost" className="text-amber-400 text-xs h-7 hover:bg-amber-500/10">
                      Ver <ChevronRight className="w-3 h-3 ml-0.5" />
                    </Button>
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* INFO ALERTS (compact) */}
      {infoAlerts.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2" data-testid="info-alerts">
          {infoAlerts.map((alert) => (
            <span key={`info-${alert.message}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/5 border border-emerald-500/10 text-emerald-400 text-xs">
              <Info className="w-3 h-3" /> {alert.message}
            </span>
          ))}
        </div>
      )}

      {/* KPI Cards Row 1 */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        <KPICard label="Usuarios" value={metrics?.users?.total} sub={`+${metrics?.users?.today || 0} hoy`} icon={Users} color="text-blue-400" />
        <KPICard label="Sellers" value={metrics?.users?.sellers} icon={TrendingUp} color="text-emerald-400" />
        <KPICard label="Listings" value={metrics?.content?.listings} sub={`${metrics?.content?.demos || 0} demo`} icon={Package} color="text-purple-400" />
        <KPICard label="Subastas" value={metrics?.content?.auctions} icon={Gavel} color="text-amber-400" />
        <KPICard label="Camadas" value={metrics?.content?.clutches} icon={Heart} color="text-pink-400" />
        <KPICard label="IA Activas" value={metrics?.ai?.active_users} sub={`${metrics?.ai?.convos_today || 0} convos hoy`} icon={Bot} color="text-cyan-400" />
      </div>

      {/* KPI Cards Row 2 - Subscriptions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KPICard label="Free" value={metrics?.subscriptions?.free} icon={CreditCard} color="text-zinc-400" />
        <KPICard label="Basic" value={metrics?.subscriptions?.basic} icon={CreditCard} color="text-emerald-400" />
        <KPICard label="Premium" value={metrics?.subscriptions?.premium} icon={CreditCard} color="text-purple-400" />
        <KPICard label="Elite" value={metrics?.subscriptions?.elite} icon={CreditCard} color="text-amber-400" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Quick Actions */}
        <div className="lg:col-span-1">
          <div className="rounded-xl border border-white/[0.04] bg-[#0A0A0D] p-5">
            <h3 className="text-sm font-medium text-zinc-300 mb-4 flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-400" /> Acciones rapidas
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <QuickAction label="Morphs" count={quickStats?.morphs_pending} href="/admin/morphs" icon={Dna} />
              <QuickAction label="Enbixos" count={quickStats?.enbixos_pending} href="/admin/enbixos" icon={Truck} />
              <QuickAction label="Demos" count={quickStats?.demos} href="/admin/moderation" icon={Trash2} />
              <QuickAction label="Disputas" count={quickStats?.disputes} href="/admin/moderation" icon={AlertTriangle} />
              <QuickAction label="Soporte" count={quickStats?.tickets_open} href="/admin/support" icon={Shield} />
              <QuickAction label="Usuarios" href="/admin/users" icon={Users} />
            </div>
          </div>

          {/* AI Cost Center (mini) */}
          {aiData && (
            <div className="rounded-xl border border-white/[0.04] bg-[#0A0A0D] p-5 mt-4" data-testid="ai-cost-widget">
              <h3 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
                <Bot className="w-4 h-4 text-cyan-400" /> IA Centro de Costo
              </h3>
              <div className="space-y-2.5">
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Usuarios con IA</span>
                  <span className="text-zinc-200">{aiData.total_ai_users}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Mensajes este mes</span>
                  <span className="text-zinc-200">{aiData.total_responses_month}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Cerca del limite</span>
                  <span className={`${aiData.near_limit_count > 0 ? 'text-amber-400' : 'text-zinc-200'}`}>{aiData.near_limit_count}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Costo estimado mes</span>
                  <span className="text-zinc-200">${aiData.estimated_cost_month} USD</span>
                </div>
                <Link to="/admin/ai" className="block mt-2">
                  <Button variant="ghost" size="sm" className="w-full text-cyan-400 text-xs h-8 hover:bg-cyan-500/5">
                    Ver detalle IA <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* Enbixos Widget */}
          <div className="rounded-xl border border-white/[0.04] bg-[#0A0A0D] p-5 mt-4">
            <h3 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
              <Truck className="w-4 h-4 text-orange-400" /> Enbixos hoy
            </h3>
            <div className="space-y-2.5">
              <div className="flex justify-between text-xs">
                <span className="text-zinc-500">Pagos sin revisar</span>
                <span className={`${(metrics?.enbixos?.paid_unreviewed || 0) > 0 ? 'text-amber-400' : 'text-zinc-200'}`}>
                  {metrics?.enbixos?.paid_unreviewed || 0}
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-zinc-500">Pendientes pago</span>
                <span className="text-zinc-200">{metrics?.enbixos?.pending || 0}</span>
              </div>
              <Link to="/admin/enbixos" className="block mt-2">
                <Button variant="ghost" size="sm" className="w-full text-orange-400 text-xs h-8 hover:bg-orange-500/5">
                  Ver Enbixos <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Activity Feed */}
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-white/[0.04] bg-[#0A0A0D] p-5">
            <h3 className="text-sm font-medium text-zinc-300 mb-4 flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-400" /> Actividad reciente
            </h3>
            <div className="space-y-1" data-testid="activity-feed">
              {activity.length === 0 ? (
                <p className="text-xs text-zinc-600 py-8 text-center">Sin actividad reciente</p>
              ) : (
                activity.slice(0, 15).map((event, i) => {
                  const Icon = EVENT_ICONS[event.type] || Activity;
                  return (
                    <div key={event.id || `${event.type}-${event.at}-${i}`} className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-white/[0.015] transition-colors">
                      <div className="w-7 h-7 rounded-lg bg-white/[0.03] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon className="w-3.5 h-3.5 text-zinc-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-zinc-300 truncate">{event.message}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {event.detail && <span className="text-[10px] text-zinc-600">{event.detail}</span>}
                          {event.at && <span className="text-[10px] text-zinc-700">{new Date(event.at).toLocaleDateString('es-MX', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Health Breakdown */}
          {health && (
            <div className="rounded-xl border border-white/[0.04] bg-[#0A0A0D] p-5 mt-4" data-testid="health-breakdown">
              <h3 className="text-sm font-medium text-zinc-300 mb-4 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-400" /> Salud de la plataforma
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <HealthBar label="Pagos" value={health.payment_health} />
                <HealthBar label="Fraude" value={100 - health.fraud_risk} />
                <HealthBar label="Compliance" value={health.compliance} />
                <HealthBar label="IA" value={health.ai_stability} />
              </div>
            </div>
          )}

          {/* Top AI Consumers */}
          {aiData?.users?.length > 0 && (
            <div className="rounded-xl border border-white/[0.04] bg-[#0A0A0D] p-5 mt-4" data-testid="top-ai-users">
              <h3 className="text-sm font-medium text-zinc-300 mb-3">Top consumidores IA</h3>
              <div className="space-y-2">
                {aiData.users.slice(0, 5).map((u, i) => (
                  <div key={u.user_id} className="flex items-center gap-3 text-xs">
                    <span className="text-zinc-600 w-4">#{i + 1}</span>
                    <span className="text-zinc-300 flex-1 truncate">{u.name || u.email}</span>
                    <span className="text-zinc-500">{u.responses_this_month}/{u.max_monthly}</span>
                    <div className="w-16 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                      <div className={`h-full rounded-full ${u.usage_pct >= 80 ? 'bg-red-400' : u.usage_pct >= 50 ? 'bg-amber-400' : 'bg-emerald-400'}`}
                        style={{ width: `${Math.min(u.usage_pct, 100)}%` }} />
                    </div>
                    <span className={`w-10 text-right ${u.usage_pct >= 80 ? 'text-red-400' : 'text-zinc-500'}`}>{u.usage_pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Moderation summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <MiniStat label="Disputas" value={metrics?.moderation?.disputes || 0} color={metrics?.moderation?.disputes > 0 ? 'text-red-400' : 'text-zinc-400'} />
            <MiniStat label="Strikes" value={metrics?.moderation?.strikes || 0} color="text-zinc-400" />
            <MiniStat label="Morphs pending" value={metrics?.moderation?.morphs_pending || 0} color={metrics?.moderation?.morphs_pending > 0 ? 'text-amber-400' : 'text-zinc-400'} />
            <MiniStat label="Reportes" value={metrics?.moderation?.reports_open || 0} color={metrics?.moderation?.reports_open > 0 ? 'text-red-400' : 'text-zinc-400'} />
          </div>
        </div>
      </div>
    </div>
  );
}

function KPICard({ label, value, sub, icon: Icon, color }) {
  return (
    <div className="rounded-xl border border-white/[0.04] bg-[#0A0A0D] p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-[10px] text-zinc-600 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-xl font-semibold text-zinc-100">{value ?? '-'}</p>
      {sub && <p className="text-[10px] text-zinc-600 mt-0.5">{sub}</p>}
    </div>
  );
}

function QuickAction({ label, count, href, icon: Icon }) {
  return (
    <Link to={href} className="flex items-center gap-2 p-2.5 rounded-lg bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.04] transition-colors group">
      <Icon className="w-3.5 h-3.5 text-zinc-500 group-hover:text-zinc-300" />
      <span className="text-xs text-zinc-400 flex-1 group-hover:text-zinc-200">{label}</span>
      {count > 0 && (
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-400 font-medium">{count}</span>
      )}
    </Link>
  );
}

function HealthBar({ label, value }) {
  const color = value >= 80 ? 'bg-emerald-400' : value >= 50 ? 'bg-amber-400' : 'bg-red-400';
  const textColor = value >= 80 ? 'text-emerald-400' : value >= 50 ? 'text-amber-400' : 'text-red-400';
  return (
    <div>
      <div className="flex justify-between mb-1">
        <span className="text-[10px] text-zinc-500">{label}</span>
        <span className={`text-[10px] font-medium ${textColor}`}>{value}%</span>
      </div>
      <div className="w-full h-1.5 rounded-full bg-zinc-800">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div className="rounded-xl border border-white/[0.04] bg-[#0A0A0D] p-3 text-center">
      <p className={`text-lg font-semibold ${color}`}>{value}</p>
      <p className="text-[10px] text-zinc-600">{label}</p>
    </div>
  );
}
